// Saisie joueur : flèches clavier, souris traînée au doigt (ou au clic maintenu), glissement à deux
// doigts sur le trackpad, et clic souris sur une case.
// Le jeu décide si un mouvement est valide : onMove (une direction), onDragToward (un point visé,
// en cases), onSwipeStep (une intention de glissement), onClickCell (une case visée).
import { trackWheelGesture } from '../core/level.js';
import { playSwipe, swipeStepPx } from '../core/swipe.js';
import {
  WHEEL_THRESHOLD_FACTOR, WHEEL_GESTURE_GAP_MS, WHEEL_INERTIA_RATIO, GRAB_START_PX,
} from '../config/constants.js';

const KEY_TO_DIRECTION = {
  ArrowUp: 'up',
  ArrowRight: 'right',
  ArrowDown: 'down',
  ArrowLeft: 'left',
};

/**
 * @param {HTMLCanvasElement} canvas
 * @param {object} handlers
 * @param {(dir: string) => boolean} handlers.onMove  pas au clavier
 * @param {(point: {x:number, y:number}) => void} handlers.onDragToward
 *   souris traînée : point visé, en cases continues (la souris y va librement, sans traverser de mur)
 * @param {() => void} [handlers.onDragEnd]  fin du geste : la souris se pose sur sa case
 * @param {() => {x:number, y:number}} handlers.getGrabPoint  centre affiché de la souris, en cases
 *   continues (début du geste)
 * @param {(intent: {primary:string, secondary:string|null}) => string|null} handlers.onSwipeStep
 *   pas de glissement à deux doigts au trackpad : le jeu choisit la direction possible et la renvoie
 * @param {(cell: {x:number,y:number}) => void} handlers.onClickCell  case visée par un clic souris
 * @param {(pressed: boolean) => void} [handlers.onPressChange]  geste en cours (halo autour de la souris)
 * @param {() => boolean} handlers.isActive  true quand le jeu accepte les déplacements
 * @param {() => number} handlers.getSwipeThreshold  glissement trackpad max pour un pas (px)
 * @param {() => number} handlers.getGridSize  nombre de cases par côté
 */
export function bindInput(canvas, {
  onMove, onDragToward, onDragEnd = () => {}, getGrabPoint, onSwipeStep, onClickCell, onPressChange, isActive, getSwipeThreshold, getGridSize,
}) {
  const onKeyDown = (e) => {
    // Ne pas confisquer les flèches quand l'utilisateur tape dans un champ
    if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;
    const dir = KEY_TO_DIRECTION[e.key];
    if (!dir) return;
    e.preventDefault();
    if (isActive()) onMove(dir);
  };

  const cellPx = () => {
    const gridSize = getGridSize();
    const width = canvas.getBoundingClientRect().width;
    return gridSize > 0 && width > 0 ? width / gridSize : 0;
  };

  // --- Souris traînée (doigt, clic maintenu à la souris ou au trackpad) ----------------------
  // Le doigt déplace un point virtuel, accroché au centre de la souris au début du geste : la souris
  // suit le doigt 1:1 (même décalage tout au long du geste, nul si on l'a prise dessous), librement,
  // et se pose sur sa case au relâchement. Pointer Events : un seul code pour le tactile, la souris
  // et le trackpad.
  let grab = null; // { id, type, startX, startY, origin: {x,y}, dragging }

  const endGrab = () => {
    if (!grab) return;
    if (grab.dragging) {
      onPressChange?.(false);
      onDragEnd();
    }
    if (canvas.hasPointerCapture?.(grab.id)) canvas.releasePointerCapture(grab.id);
    grab = null;
  };

  const onPointerDown = (e) => {
    if (grab || !isActive()) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const origin = getGrabPoint();
    grab = {
      id: e.pointerId,
      type: e.pointerType,
      startX: e.clientX,
      startY: e.clientY,
      origin: { x: origin.x, y: origin.y },
      // Au doigt, le geste est un glisser dès l'appui (pas de tap) ; à la souris, après quelques px
      dragging: e.pointerType !== 'mouse',
    };
    // Le geste reste suivi même si le doigt sort du labyrinthe
    try { canvas.setPointerCapture?.(e.pointerId); } catch { /* pointeur déjà relâché */ }
    if (grab.dragging) onPressChange?.(true);
  };

  const onPointerMove = (e) => {
    if (!grab || e.pointerId !== grab.id) return;
    e.preventDefault();
    const dx = e.clientX - grab.startX;
    const dy = e.clientY - grab.startY;
    if (!grab.dragging) {
      if (Math.hypot(dx, dy) < GRAB_START_PX) return;
      grab.dragging = true;
      onPressChange?.(true);
    }
    const size = cellPx();
    if (!size || !isActive()) return;
    onDragToward({ x: grab.origin.x + dx / size, y: grab.origin.y + dy / size });
  };

  // Clic souris sans glisser : la souris avance vers la case visée (voir game.moveTo). Au doigt,
  // un simple tap ne fait rien (évite les déplacements involontaires).
  const clickCell = (e) => {
    const gridSize = getGridSize();
    if (gridSize <= 0) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = Math.floor(((e.clientX - rect.left) / rect.width) * gridSize);
    const y = Math.floor(((e.clientY - rect.top) / rect.height) * gridSize);
    if (x < 0 || y < 0 || x >= gridSize || y >= gridSize) return;
    onClickCell({ x, y });
  };

  const onPointerUp = (e) => {
    if (!grab || e.pointerId !== grab.id) return;
    const wasClick = grab.type === 'mouse' && !grab.dragging;
    endGrab();
    if (wasClick && isActive()) clickCell(e);
  };

  const onPointerCancel = (e) => {
    if (grab && e.pointerId === grab.id) endGrab();
  };

  // --- Glissement à deux doigts au trackpad ---------------------------------------------------
  // Sans cliquer, le trackpad produit des événements wheel : le trajet cumulé est joué case par
  // case (core/swipe.js), l'inertie de macOS est écartée (trackWheelGesture), halo pendant le geste.
  let wheelGesture = null;
  let wheelTrail = { x: 0, y: 0 };
  let wheelGlowTimer = null;

  const onWheel = (e) => {
    if (e.ctrlKey) return; // pincement à deux doigts = zoom du navigateur
    // Ni défilement de la page, ni retour arrière du navigateur sur un glissement horizontal
    e.preventDefault();
    if (!isActive()) return;
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? canvas.clientHeight : 1;
    // La souris suit les doigts : avec le défilement naturel de macOS, glisser vers le haut donne
    // un deltaY positif, d'où l'inversion
    const dx = -e.deltaX * unit;
    const dy = -e.deltaY * unit;
    const { gesture, isNew, inertia } = trackWheelGesture(wheelGesture, Math.hypot(dx, dy), e.timeStamp, {
      gapMs: WHEEL_GESTURE_GAP_MS,
      inertiaRatio: WHEEL_INERTIA_RATIO,
    });
    wheelGesture = gesture;
    if (isNew || inertia) wheelTrail = { x: 0, y: 0 };
    if (inertia) return;

    onPressChange?.(true);
    clearTimeout(wheelGlowTimer);
    wheelGlowTimer = setTimeout(() => onPressChange?.(false), WHEEL_GESTURE_GAP_MS);

    const stepPx = swipeStepPx(cellPx() || Infinity, getSwipeThreshold()) * WHEEL_THRESHOLD_FACTOR;
    wheelTrail = playSwipe({ x: wheelTrail.x + dx, y: wheelTrail.y + dy }, stepPx, onSwipeStep);
  };

  window.addEventListener('keydown', onKeyDown);
  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerCancel);
  canvas.addEventListener('wheel', onWheel, { passive: false });

  return () => {
    endGrab();
    clearTimeout(wheelGlowTimer);
    window.removeEventListener('keydown', onKeyDown);
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerCancel);
    canvas.removeEventListener('wheel', onWheel);
  };
}
